import { ChangeDetectionStrategy, Component, computed, effect, inject, Injector, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { datadogLogs } from '@datadog/browser-logs';
import { environment } from '@env/environment';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, map, switchMap } from 'rxjs/operators';

import { Logger } from './core/logger.service';
import { isMembershipMissing } from './shared/authentication/oauth-error-redirect';
import { AuthFailureService } from './shared/authentication/services/auth-failure.service';
import { HypecastOAuthService } from './shared/authentication/services/hypecast-oauth.service';
import { ApiUrlService } from './shared/services/api-url.service';
import { ForbiddenService } from './shared/services/forbidden.service';
import { ITenantService } from './shared/services/itenant.service';

const _log = new Logger('AppComponent');

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [RouterOutlet, TranslatePipe],
})
export class AppComponent {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly titleService = inject(Title);
  private readonly tenantService = inject(ITenantService);
  private readonly apiUrlService = inject(ApiUrlService);
  private readonly forbiddenService = inject(ForbiddenService);
  private readonly authFailure = inject(AuthFailureService);
  private readonly injector = inject(Injector);

  readonly tenant = this.tenantService.tenantSignal;
  readonly authFailed = toSignal(this.authFailure.failed$, { initialValue: false });
  readonly accessDenied = this.authFailure.accessDenied;

  /** The ID server's reason in small print, except for the plain "not a member" case the text already explains. */
  readonly accessDeniedDetails = computed(() => {
    const denied = this.accessDenied();
    if (denied === null || isMembershipMissing(denied)) {
      return null;
    }
    return denied.description ?? denied.error;
  });

  readonly switchingAccount = signal(false);

  private readonly navigationStart = toSignal<NavigationStart | null>(
    this.router.events.pipe(filter((event): event is NavigationStart => event instanceof NavigationStart)),
    { initialValue: null },
  );

  private readonly routeTitle = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => {
        let route = this.activatedRoute;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      switchMap((route) => route.data),
      map((routeData) => routeData['title'] as string | undefined),
    ),
    { initialValue: undefined },
  );

  constructor() {
    void this.tenantService.loadTenant().catch((error: unknown) => _log.error('Unable to resolve tenant', error));

    effect(() => {
      const tenant = this.tenant();
      if (!tenant) {
        return;
      }

      this.apiUrlService.getTenant = () => tenant;
      datadogLogs.setGlobalContext({
        application: `${tenant.tenantName} Admin Console`,
        tenant: tenant.tenantName,
      });
    });

    effect(() => {
      if (this.navigationStart() !== null) {
        this.forbiddenService.allow();
      }
    });

    effect(() => {
      const title = this.routeTitle();
      if (title) {
        this.titleService.setTitle(title);
      }
    });

    if (environment.production) {
      Logger.enableProductionMode();
    }

    this.clearTableRelatedLocalStorage();
  }

  signInWithDifferentAccount(): void {
    this.switchingAccount.set(true);
    // Resolved on demand: the OAuth service starts the login flow when it is created.
    void this.injector
      .get(HypecastOAuthService)
      .signInWithDifferentAccount()
      .catch((error: unknown) => {
        _log.error('Could not end the identity provider session', error);
        this.switchingAccount.set(false);
      });
  }

  retryLogin(): void {
    this.authFailure.retry();
  }

  private clearTableRelatedLocalStorage(): void {
    Object.keys(localStorage)
      .filter((key) => key.includes('table'))
      .forEach((key) => {
        localStorage.removeItem(key);
        _log.debug(`Removed localStorage item: ${key}`);
      });
  }
}
